import React, { useState, useMemo } from "react";
import { base44 } from "@/race-tracker/api/rtClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/race-tracker/components/ui/table";
import { Badge } from "@/race-tracker/components/ui/badge";
import { Button } from "@/race-tracker/components/ui/button";
import { Download } from "lucide-react";
import { getSweepStaleness, SWEEP_LEVEL_STYLES } from "@/race-tracker/lib/sweepUtils";
import { format } from "date-fns";
import FilterBar from "../components/filters/FilterBar";
import RaceDetailModal from "../components/modals/RaceDetailModal";

const STALENESS_THRESHOLD = { warning: 7, danger: 14, critical: 30 };

export default function ListView() {
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    search: "", da: "all", ae: "all", am: "all", state: "all",
    chamber: "all", election_type: "all", status: "Active", smg_plus: false, agency: "all",
    sweep_staleness: "all",
  });
  const [selectedRace, setSelectedRace] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "state", dir: "asc" });

  const { data: advertisers = [] } = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => base44.entities.Advertiser.list("-created_date", 500),
  });

  const { data: races = [] } = useQuery({
    queryKey: ["races"],
    queryFn: () => base44.entities.Race.list("-created_date", 5000),
  });

  const { data: opponents = [] } = useQuery({
    queryKey: ["opponents"],
    queryFn: () => base44.entities.Opponent.list("-created_date", 10000),
  });

  const adsMap = useMemo(() => {
    const map = {};
    advertisers.forEach((a) => { map[a.id] = a; });
    return map;
  }, [advertisers]);

  const opponentsByRace = useMemo(() => {
    const map = {};
    opponents.forEach((o) => {
      if (!map[o.race_id]) map[o.race_id] = [];
      map[o.race_id].push(o);
    });
    return map;
  }, [opponents]);

  const rows = useMemo(() => {
    return races.map((race) => {
      const ad = adsMap[race.advertiser_id];
      if (!ad) return null;
      const raceOpponents = opponentsByRace[race.id] || [];
      return {
        ...race,
        advertiser_name: ad.advertiser_name,
        candidate_name: ad.candidate_name,
        agency: ad.agency,
        account_executive: ad.account_executive,
        account_manager: ad.account_manager,
        data_analyst: ad.data_analyst,
        smg_plus: ad.smg_plus,
        ad_status: ad.status,
        opponents_text: raceOpponents.map((o) => `${o.opponent_name} (${o.party})`).join(" | "),
        ballotpedia_links: raceOpponents.map((o) => o.ballotpedia_link).filter(Boolean).join(" | "),
        _opponent_count: raceOpponents.length,
      };
    }).filter(Boolean);
  }, [races, adsMap, opponentsByRace]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const search = filters.search?.toLowerCase();
      if (search) {
        const match = r.advertiser_name?.toLowerCase().includes(search) ||
          r.candidate_name?.toLowerCase().includes(search) ||
          r.state?.toLowerCase().includes(search) ||
          r.district?.toLowerCase().includes(search) ||
          r.opponents_text?.toLowerCase().includes(search);
        if (!match) return false;
      }
      if (filters.da !== "all" && r.data_analyst !== filters.da) return false;
      if (filters.ae !== "all" && r.account_executive !== filters.ae) return false;
      if (filters.am !== "all" && r.account_manager !== filters.am) return false;
      if (filters.state !== "all" && r.state !== filters.state) return false;
      if (filters.chamber !== "all" && r.chamber !== filters.chamber) return false;
      if (filters.election_type !== "all" && r.election_type !== filters.election_type) return false;
      if (filters.agency !== "all" && r.agency !== filters.agency) return false;
      if (filters.smg_plus && !r.smg_plus) return false;
      if (filters.status === "Active" && r.status === "Done") return false;
      if (filters.status !== "all" && filters.status !== "Active" && r.status !== filters.status) return false;
      if (filters.sweep_staleness !== "all") {
        const threshold = STALENESS_THRESHOLD[filters.sweep_staleness];
        const { days } = getSweepStaleness(r.last_digital_sweep);
        if (days === null || days <= threshold) return false;
      }

      return true;
    });
  }, [rows, filters]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
  };

  const handleExport = () => {
    const headers = [
      "Status", "Agency", "State", "Office", "Race", "Team", "Advertiser Name", "Candidate Name",
      "Account Executive", "Account Manager", "Data Analyst", "SMG+",
      "Primary Date", "Runoff Date", "Markets", "Race Importance Rating", "General Election Rating",
      "Reference Link", "Opponents", "Ballotpedia Links",
      "Last Digital Sweep", "Last Linear Sweep", "Race Notes", "Notes Last Updated"
    ];
    const csvRows = [headers.join(",")];

    sorted.forEach((r) => {
      const row = [
        esc(r.status),
        esc(r.agency),
        r.state || "",
        esc(r.chamber),
        esc(r.district),
        esc(r.team),
        esc(r.advertiser_name),
        esc(r.candidate_name),
        esc(r.account_executive),
        esc(r.account_manager),
        esc(r.data_analyst),
        r.smg_plus ? "Yes" : "No",
        r.election_date || "",
        r.runoff_date || "",
        esc(r.markets),
        r.race_importance || "",
        esc(r.ge_rating),
        esc(r.reference_link),
        esc(r.opponents_text),
        esc(r.ballotpedia_links),
        r.last_digital_sweep || "",
        r.last_linear_sweep || "",
        esc(r.notes),
        r.notes_updated_at ? format(new Date(r.notes_updated_at), "yyyy-MM-dd HH:mm") : "",
      ];
      csvRows.push(row.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `race_intelligence_export_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortHeader = ({ label, field }) => (
    <TableHead
      className="text-[10px] uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none whitespace-nowrap"
      onClick={() => handleSort(field)}
    >
      {label} {sortConfig.key === field ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">List View</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{sorted.length} race{sorted.length !== 1 ? "s" : ""}</p>
        </div>
        <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleExport}>
          <Download className="w-3.5 h-3.5" /> Export CSV
        </Button>
      </div>

      <FilterBar filters={filters} onChange={setFilters} />

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <SortHeader label="Status" field="status" />
              <SortHeader label="Agency" field="agency" />
              <SortHeader label="Advertiser" field="advertiser_name" />
              <SortHeader label="Candidate" field="candidate_name" />
              <SortHeader label="Data Analyst" field="data_analyst" />
              <SortHeader label="State" field="state" />
              <SortHeader label="Chamber" field="chamber" />
              <SortHeader label="District" field="district" />
              <SortHeader label="Team" field="team" />
              <SortHeader label="Election" field="election_type" />
              <SortHeader label="Primary Date" field="election_date" />
              <SortHeader label="GE Rating" field="ge_rating" />
              <SortHeader label="Dig. Sweep" field="last_digital_sweep" />
              <SortHeader label="Lin. Sweep" field="last_linear_sweep" />
              <TableHead className="text-[10px] uppercase tracking-wider">Opponents</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} className="text-center py-12 text-sm text-muted-foreground">
                  No races match your filters
                </TableCell>
              </TableRow>
            ) : sorted.map((r) => (
              <TableRow
                key={r.id}
                className={`cursor-pointer hover:bg-accent/50 transition-colors ${r.status === "Done" ? "opacity-40" : r.status === "Inactive" ? "opacity-60" : ""}`}
                onClick={() => setSelectedRace(r)}
              >
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                    r.status === "Active" ? "bg-emerald-500/10 text-emerald-600 border-emerald-200" :
                    r.status === "Inactive" ? "bg-amber-50 text-amber-600 border-amber-300" :
                    "text-muted-foreground"
                  }`}>{r.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.agency || "—"}</TableCell>
                <TableCell className="text-xs font-medium max-w-[140px] truncate">{r.advertiser_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">{r.candidate_name || "—"}</TableCell>
                <TableCell className="text-xs">{r.data_analyst}</TableCell>
                <TableCell className="text-xs font-mono font-medium">{r.state}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.chamber}</TableCell>
                <TableCell className="text-xs font-mono">{r.district || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">{r.team || "—"}</TableCell>
                <TableCell className="text-xs">{r.election_type || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {r.election_date ? format(new Date(r.election_date), "MMM d") : "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.ge_rating || "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {(() => {
                    const sweep = getSweepStaleness(r.last_digital_sweep);
                    const s = SWEEP_LEVEL_STYLES[sweep.level];
                    return (
                      <span className={`flex items-center gap-1 ${s.text}`}>
                        <span className={`w-2 h-2 rounded-full shrink-0 ${s.dot}`} />
                        {r.last_digital_sweep ? format(new Date(r.last_digital_sweep), "MMM d") : "—"}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {r.last_linear_sweep ? format(new Date(r.last_linear_sweep), "MMM d") : "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                  {r.opponents_text || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedRace && (
        <RaceDetailModal
          race={selectedRace}
          open={!!selectedRace}
          onClose={() => setSelectedRace(null)}
          onSave={() => {
            setSelectedRace(null);
            queryClient.invalidateQueries({ queryKey: ["races"] });
          }}
          onDelete={async (id) => {
            await base44.entities.Race.delete(id);
            setSelectedRace(null);
            queryClient.invalidateQueries({ queryKey: ["races"] });
          }}
        />
      )}
    </div>
  );
}

function esc(val) {
  if (!val) return "";
  const str = String(val);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}